// App configuration with feature flags based on license type
export type LicenseType = 'offline' | 'cloud';

export interface AppFeatures {
  // Sync features
  enableSync: boolean;
  autoSync: boolean;
  manualSync: boolean;
  
  // Cloud features
  multiDevice: boolean;
  cloudBackup: boolean;
  serverConnection: boolean;
  
  // Local features (available to both)
  localBackup: boolean;
  localRestore: boolean;
  exportData: boolean;
  importData: boolean;
  
  // General
  offlineMode: boolean;
}

export interface AppConfig {
  mode: LicenseType;
  serverUrl: string;
  features: AppFeatures;
}

// Default configuration based on environment variable
const getModeFromEnv = (): LicenseType => {
  return (process.env.REACT_APP_MODE as LicenseType) || 'cloud';
};

const getServerUrlFromEnv = (): string => {
  return process.env.REACT_APP_SERVER_URL || 'https://pos.hmwebs.co.uk';
};

// Feature flags based on license type
const getFeatures = (mode: LicenseType): AppFeatures => {
  if (mode === 'offline') {
    return {
      // Sync features - disabled for offline
      enableSync: false,
      autoSync: false,
      manualSync: false,
      
      // Cloud features - disabled for offline
      multiDevice: false,
      cloudBackup: false,
      serverConnection: false,
      
      // Local features - enabled for both
      localBackup: true,
      localRestore: true,
      exportData: true,
      importData: true,
      
      // General
      offlineMode: true,
    };
  } else {
    // Cloud mode
    return {
      // Sync features - enabled for cloud
      enableSync: true,
      autoSync: true,
      manualSync: true,
      
      // Cloud features - enabled for cloud
      multiDevice: true,
      cloudBackup: true,
      serverConnection: true,
      
      // Local features - enabled for both
      localBackup: true,
      localRestore: true,
      exportData: true,
      importData: true,
      
      // General
      offlineMode: false,
    };
  }
};

export const appConfig: AppConfig = {
  mode: getModeFromEnv(),
  serverUrl: getServerUrlFromEnv(),
  features: getFeatures(getModeFromEnv()),
};

// Helper function to check if a feature is enabled
export const isFeatureEnabled = (feature: keyof AppFeatures): boolean => {
  return appConfig.features[feature];
};

// Helper function to get current mode
export const getMode = (): LicenseType => {
  return appConfig.mode;
};

// Helper function to check if running in offline mode
export const isOfflineMode = (): boolean => {
  return appConfig.mode === 'offline';
};
